import React from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { toast } from "sonner";
// Media removed for now as API payload doesn't include it
import { axiosPrivate } from "@/api/axios";
import { useMutation, useQueryClient } from "@tanstack/react-query";

// Word count helper (after trimming and splitting by spaces)
const wordCount = (val: string) => (val || "").trim().split(/\s+/).filter(word => word.length > 0).length;

// Daily Challenge Schema aligned to API
const challengeSchema = z.object({
  title: z
    .string()
    .min(1, "Title is required")
    .refine((v) => wordCount(v) <= 15, { message: "Title max 15 words" }),
  description: z
    .string()
    .min(1, "Description is required")
    .refine((v) => wordCount(v) <= 30, { message: "Description max 30 words" }),
  duration: z.number().min(0, "Duration cannot be negative"),
  reward_time: z.number().min(0, "Reward time cannot be negative"),
});

type ChallengeForm = z.infer<typeof challengeSchema>;

interface AddChallengeDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCreated?: (challenge: any) => void; // placeholder callback
  section?: 'gut-wellness' | 'nutrition' | 'sleep-exercise';
}

const sectionDisplay: Record<string, string> = {
  'gut-wellness': 'Gut & Wellness',
  nutrition: 'Nutrition',
  'sleep-exercise': 'Sleep & Exercise',
};

const challengeCategories = [
  'Hydration',
  'Exercise',
  'Nutrition',
  'Sleep',
  'Mindfulness',
  'Gut Health',
  'Other'
];

export default function AddChallengeDialog({ open, onOpenChange, onCreated, section = 'gut-wellness' }: AddChallengeDialogProps) {
  const queryClient = useQueryClient();

  const normalizeSection = (s: string): 'gut' | 'nutrition' | 'sleep' => {
    if (s === 'nutrition') return 'nutrition';
    if (s === 'sleep-exercise') return 'sleep';
    return 'gut';
  };

  const createChallenge = useMutation({
    mutationFn: async (payload: any) => {
      const res = await axiosPrivate.post("/challenges/", payload);
      return (res.data as any)?.data ?? res.data;
    },
    onSuccess: (data) => {
      toast.success("Challenge created");
      // Refresh list
      queryClient.invalidateQueries({ queryKey: ["challenges"], exact: false });
      onCreated?.(data);
      // reset and close handled below by caller
    },
    onError: (err: any) => {
      const msg = err?.response?.data?.message || err?.message || "Failed to create challenge";
      toast.error(String(msg));
    },
  });

  const form = useForm<ChallengeForm>({
    resolver: zodResolver(challengeSchema),
    defaultValues: {
      title: "",
      description: "",
      duration: 0,
      reward_time: 0,
    },
    mode: "onChange",
  });

  function handleSubmit(values: ChallengeForm) {
    const type = normalizeSection(section);
    const payload = {
      title: values.title,
      description: values.description,
      type,
      duration: values.duration,
      duration_type: "day", // Hardcoded to days
      reward_time: values.reward_time,
      reward_time_type: "day", // Hardcoded to days (changed from minute)
    };

    createChallenge.mutate(payload, {
      onSuccess: () => {
        form.reset({
          title: "",
          description: "",
          duration: 0,
          reward_time: 0,
        });
        onOpenChange(false);
      },
    });
  }

  const titleWords = wordCount(form.watch("title"));
  const descriptionWords = wordCount(form.watch("description"));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="!w-[95vw] sm:!w-full !max-w-[560px] sm:!max-w-[720px] !max-h-[90vh] overflow-y-auto p-4 sm:p-6">
        <DialogHeader>
          <DialogTitle>Add {sectionDisplay[section] || 'Challenge'} Challenge</DialogTitle>
          <DialogDescription>Create a daily challenge for {sectionDisplay[section]}. Title max 15 words, Description max 30 words.</DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form className="space-y-6" onSubmit={form.handleSubmit(handleSubmit)}>
            <FormField name="title" control={form.control} render={({ field }) => (
              <FormItem>
                <FormLabel className="after:content-['*'] after:text-red-500 after:ml-1">Title <span className="text-xs text-muted-foreground">({titleWords}/15 words)</span></FormLabel>
                <FormControl>
                  <Input placeholder="Drink 8 glasses of water today" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )} />
            
            <FormField name="description" control={form.control} render={({ field }) => (
              <FormItem>
                <FormLabel className="after:content-['*'] after:text-red-500 after:ml-1">Description <span className="text-xs text-muted-foreground">({descriptionWords}/30 words)</span></FormLabel>
                <FormControl>
                  <Textarea rows={3} placeholder="Stay hydrated throughout the day by drinking plenty of water to support your overall health and wellness" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )} />

            <FormField name="duration" control={form.control} render={({ field }) => (
              <FormItem>
                <FormLabel>Challenge Duration (Days)</FormLabel>
                <FormControl>
                  <Input
                    type="number"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    min="0"
                    placeholder="e.g., 7 or 30"
                    {...field}
                    onChange={(e) => field.onChange(parseInt(e.target.value) || 0)}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )} />

            <FormField name="reward_time" control={form.control} render={({ field }) => (
              <FormItem>
                <FormLabel>Discount Reward Time (Days)</FormLabel>
                <FormControl>
                  <Input
                    type="number"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    min="0"
                    placeholder="e.g., 5 or 10"
                    {...field}
                    onChange={(e) => field.onChange(parseInt(e.target.value) || 0)}
                  />
                </FormControl>
                <p className="text-xs text-muted-foreground">Discount time users earn for completing this challenge</p>
                <FormMessage />
              </FormItem>
            )} />

            <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2">
              <Button className="w-full sm:w-auto" type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={createChallenge.isPending}>Cancel</Button>
              <Button className="w-full sm:w-auto" type="submit" disabled={!form.formState.isValid || createChallenge.isPending}>
                {createChallenge.isPending ? "Adding..." : "Add Challenge"}
              </Button>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
